import { useEffect, useRef, useState } from 'react';
import { Mic } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/custom/IconButton';

interface SpeechResultEvent {
	resultIndex: number;
	results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
}

interface BrowserSpeechRecognition {
	continuous: boolean;
	interimResults: boolean;
	lang: string;
	grammars?: object;
	onresult: ((event: SpeechResultEvent) => void) | null;
	onerror: ((event: { error: string }) => void) | null;
	onend: (() => void) | null;
	start: () => void;
	stop: () => void;
	abort: () => void;
}

type SpeechRecognitionCtor = new () => BrowserSpeechRecognition;

const getSpeechRecognition = (): SpeechRecognitionCtor | null => {
	if (typeof window === 'undefined') return null;
	const speechWindow = window as Window & {
		SpeechRecognition?: SpeechRecognitionCtor;
		webkitSpeechRecognition?: SpeechRecognitionCtor;
	};
	return speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition || null;
};

const joinSpokenText = (current: string, spoken: string, maxLength: number) => {
	const addition = spoken.trim();
	if (!addition) return current.slice(0, maxLength);
	const base = current.replace(/\s+$/, '');
	const combined = base ? `${base} ${addition}` : addition;
	return combined.slice(0, maxLength);
};

interface TalkButtonProps {
	value: string;
	onChange: (next: string) => void;
	onListeningChange?: (listening: boolean) => void;
	disabled?: boolean;
	maxLength?: number;
	prominent?: boolean;
	phrases?: string[];
	correctText?: (text: string) => string;
}

const TalkButton = ({
	value,
	onChange,
	onListeningChange,
	disabled = false,
	maxLength = 1600,
	prominent = false,
	phrases,
	correctText,
}: TalkButtonProps) => {
	const [listening, setListening] = useState(false);
	const supported = Boolean(getSpeechRecognition());
	const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
	const valueRef = useRef(value);
	const latestTextRef = useRef(value);
	const spokenBaseRef = useRef('');
	const keepListeningRef = useRef(false);
	const lastStartRef = useRef(0);
	const onChangeRef = useRef(onChange);
	const phrasesRef = useRef(phrases);
	const correctTextRef = useRef(correctText);
	const correctLatestRef = useRef<() => void>(() => {});

	valueRef.current = value;
	if (!keepListeningRef.current) latestTextRef.current = value;
	onChangeRef.current = onChange;
	phrasesRef.current = phrases;
	correctTextRef.current = correctText;
	const onListeningChangeRef = useRef(onListeningChange);
	onListeningChangeRef.current = onListeningChange;

	const updateListening = (next: boolean) => {
		setListening(next);
		onListeningChangeRef.current?.(next);
	};

	const stopListening = () => {
		keepListeningRef.current = false;
		try {
			recognitionRef.current?.stop();
		} catch {
			// Already stopped.
		}
		correctLatestRef.current();
		updateListening(false);
	};

	useEffect(() => {
		return () => {
			keepListeningRef.current = false;
			try {
				recognitionRef.current?.abort();
			} catch {
				// Already stopped.
			}
		};
	}, []);

	useEffect(() => {
		if (disabled && keepListeningRef.current) stopListening();
	}, [disabled]);

	if (!supported) return null;

	const startListening = () => {
		const SpeechRecognition = getSpeechRecognition();
		if (!SpeechRecognition || disabled) return;

		let sessionBase = valueRef.current;
		spokenBaseRef.current = sessionBase;
		keepListeningRef.current = true;

		const recognition = recognitionRef.current || new SpeechRecognition();
		recognitionRef.current = recognition;
		recognition.continuous = true;
		recognition.interimResults = true;
		recognition.lang = navigator.language || 'en-US';
		const grammarPhrases = phrasesRef.current;
		if (grammarPhrases?.length) {
			const speechWindow = window as Window & {
				SpeechGrammarList?: new () => { addFromString: (grammar: string, weight: number) => void };
				webkitSpeechGrammarList?: new () => { addFromString: (grammar: string, weight: number) => void };
			};
			const SpeechGrammarList = speechWindow.SpeechGrammarList || speechWindow.webkitSpeechGrammarList;
			if (SpeechGrammarList) {
				const alternatives = [
					...new Set(
						grammarPhrases
							.map((phrase) =>
								phrase
									.toLowerCase()
									.replace(/[^a-z0-9\s]/g, ' ')
									.replace(/\s+/g, ' ')
									.trim()
							)
							.filter(Boolean)
					),
				];
				if (alternatives.length) {
					try {
						const list = new SpeechGrammarList();
						list.addFromString(
							`#JSGF V1.0; grammar software; public <software> = ${alternatives.join(' | ')} ;`,
							1
						);
						recognition.grammars = list;
					} catch {
						// Recognition still works without a phrase list.
					}
				}
			}
		}

		const applyCorrection = (text: string) => {
			const correct = correctTextRef.current;
			if (!correct) return text;
			const corrected = correct(text);
			return corrected ? corrected.slice(0, maxLength) : text;
		};

		const publish = (text: string) => {
			latestTextRef.current = text;
			spokenBaseRef.current = text;
			onChangeRef.current(text);
		};

		const transcriptFrom = (event?: SpeechResultEvent) => {
			if (!event) return '';
			let spoken = '';
			for (let index = 0; index < event.results.length; index += 1) {
				spoken += event.results[index][0]?.transcript || '';
			}
			return spoken;
		};

		correctLatestRef.current = () => {
			const corrected = applyCorrection(latestTextRef.current);
			if (corrected === latestTextRef.current) return;
			sessionBase = corrected;
			publish(corrected);
		};

		recognition.onresult = (event) => {
			const next = joinSpokenText(sessionBase, transcriptFrom(event), maxLength);
			publish(applyCorrection(next));
		};

		recognition.onerror = (event) => {
			if (event.error === 'aborted' || event.error === 'no-speech') return;
			keepListeningRef.current = false;
			updateListening(false);
			if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
				toast.error('Allow the microphone to use Talk. You can still type.');
				return;
			}
			toast.error('Talk did not catch that. You can still type.');
		};

		recognition.onend = () => {
			if (!keepListeningRef.current) {
				correctLatestRef.current();
				updateListening(false);
				return;
			}
			if (Date.now() - lastStartRef.current < 400) {
				keepListeningRef.current = false;
				correctLatestRef.current();
				updateListening(false);
				return;
			}
			try {
				sessionBase = latestTextRef.current;
				lastStartRef.current = Date.now();
				recognition.start();
			} catch {
				keepListeningRef.current = false;
				correctLatestRef.current();
				updateListening(false);
			}
		};

		try {
			lastStartRef.current = Date.now();
			recognition.start();
			updateListening(true);
		} catch {
			keepListeningRef.current = false;
			toast.error('Talk did not catch that. You can still type.');
		}
	};

	const label = listening ? 'Stop' : 'Talk';

	if (prominent) {
		return (
			<Button
				type="button"
				variant={listening ? 'default' : 'outline'}
				disabled={disabled}
				aria-label={listening ? 'Stop talking' : 'Talk'}
				aria-pressed={listening}
				className="shrink-0 gap-2"
				onClick={listening ? stopListening : startListening}
			>
				<Mic className="size-4" aria-hidden="true" />
				{label}
				<span className="sr-only" aria-live="polite">
					{listening ? 'Listening' : ''}
				</span>
			</Button>
		);
	}

	return (
		<IconButton
			type="button"
			selected={listening}
			disabled={disabled}
			toolTipTitle={label}
			toolTipSide="top"
			aria-label={listening ? 'Stop talking' : 'Talk'}
			aria-pressed={listening}
			onClick={listening ? stopListening : startListening}
		>
			<Mic className="size-4" aria-hidden="true" />
			<span className="sr-only" aria-live="polite">
				{listening ? 'Listening' : ''}
			</span>
		</IconButton>
	);
};

export default TalkButton;
